import { getRoomService } from '@features/room/container';
import {
  markRoomJoinedForStreamingOnly,
  useRoomStore,
} from '@features/room';
import type { RoomService } from '@features/room';
import { RoomBackedStreamPublisher } from './adapters/RoomBackedStreamPublisher';
import { LiveKitEgressPlatformStreamingService } from './adapters/LiveKitEgressPlatformStreamingService';
import type { PlatformStreamingPort } from './ports/PlatformStreamingPort';
import type { StreamPublisherPort } from './ports/StreamPublisherPort';

let streamPublisher: StreamPublisherPort | null = null;
let platformStreaming: PlatformStreamingPort | null = null;
let roomServiceRef: RoomService | null = null;

/**
 * Returns the singleton RoomService that RoomBackedStreamPublisher depends on.
 * Kept as a separate accessor so the streaming container can be reset cleanly
 * for tests without touching the room feature's own container state.
 */
function resolveRoomService(): RoomService {
  if (!roomServiceRef) {
    roomServiceRef = getRoomService();
  }
  return roomServiceRef;
}

function generateStreamingRoomName(): string {
  const adjectives = ['swift', 'bold', 'calm', 'keen', 'warm'];
  const nouns = ['river', 'peak', 'forge', 'orbit', 'spark'];
  const adj = adjectives[Math.floor(Math.random() * adjectives.length)];
  const noun = nouns[Math.floor(Math.random() * nouns.length)];
  const num = Math.floor(Math.random() * 900) + 100;
  return `stream-${adj}-${noun}-${num}`;
}

/**
 * Used by RoomBackedStreamPublisher when a user hits "Start Streaming"
 * without first joining a room on the Streaming tab. Brings up a fresh
 * LiveKit room (so platform egress has somewhere to attach) and tags it
 * `joinedForStreamingOnly` so a later stop can tear it back down without
 * kicking co-hosts.
 *
 * The Preview page is responsible for starting the local media capture
 * before this is called. If the camera is off, the resulting room has no
 * published tracks and the platform egress pipeline will simply report
 * "no input" — the surface prompts the user to enable the camera.
 */
async function ensureRoomForStreaming(): Promise<string> {
  const roomName = generateStreamingRoomName();
  await useRoomStore.getState().createAndJoin(roomName);
  markRoomJoinedForStreamingOnly();
  return roomName;
}

/**
 * Returns the singleton StreamPublisherPort instance.
 *
 * The streaming feature does NOT own a LiveKit Room — the room feature is
 * the sole owner. This publisher runs on top of the room feature's
 * RoomService and re-publishes the local stream on the already-connected
 * room. If no room is connected yet, the publisher will spin one up
 * automatically (used by the Preview tab's "Start Streaming" button).
 */
export function getStreamPublisherPort(): StreamPublisherPort {
  if (!streamPublisher) {
    streamPublisher = new RoomBackedStreamPublisher(
      resolveRoomService(),
      ensureRoomForStreaming,
    );
  }
  return streamPublisher;
}

export function getPlatformStreamingPort(): PlatformStreamingPort {
  if (!platformStreaming) {
    platformStreaming = new LiveKitEgressPlatformStreamingService();
  }
  return platformStreaming;
}

/**
 * Useful for tests.
 */
export function resetStreamingContainer(): void {
  streamPublisher = null;
  platformStreaming = null;
  roomServiceRef = null;
}
